import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AssetRatingService } from '../../../services/asset-rating.service';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { AssetRating } from '../../../models/asset-rating.model';
import { RatingUnit } from '../../../enums/rating-unit';
import { PermissionsService } from '../../../services/permissions.service';

//API calls are the same as AssetRatings CRUD
@Component({
  selector: 'lib-nec-transformer-rating',
  standalone: false,
  templateUrl: './nec-transformer-rating.component.html',
  styleUrls: ['./nec-transformer-rating.component.scss', '../network-entity-classes.component.scss'],
})
export class NecTransformerRatingComponent implements OnInit {
  @ViewChild('closeEditModal') closeEditModal!: ElementRef;
  utilityId: number = 0;
  filterOpened: boolean = false;
  searchDropdownOpened: boolean = false;
  actionMenuOpenForId: number | null = null; //for the popup, only to be shown on the row of the action menu that it is selected for
  isEdit: boolean = false;

  ratings: AssetRating[] = [];
  selectedAssetRating: AssetRating = new AssetRating();
  selectedRating: number | null = null;
  selectedRatingId: string | null = null;
  selectedUnit: string = '';
  RatingUnit = RatingUnit;

  currentPage: number = 1;
  pageSize: number = 10;
  pageSizes = [10, 20];
  count: number = 0;

  constructor(
    private toastr: ToastrService,
    private assetRatingService: AssetRatingService,
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ){}

  ngOnInit(): void {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
    if (this.utilityId != 0) {
      this.reloadTable();
    }
  }

  reloadTable(searchText?: string, page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: searchText,
      },
      utilityId: this.utilityId,
    };

    this.getAllTransformerRatings(dataIn);
  }

  private getAllTransformerRatings(dataIn: any) {
    this.assetRatingService.getAll(dataIn).subscribe({
      next: (response) => {
        this.ratings = response.data.data;
        this.count = response.data.count;
        if (this.count == 0) {
          this.toastr.info('No data for given input.');
        }
      },
      error: (_) => this.toastr.error('An error ocurred.'),
    });
  }

  toggleActionMenu(rating: AssetRating, event: MouseEvent) {
    event.stopPropagation();
    this.selectedAssetRating = rating;
    this.actionMenuOpenForId =
      this.actionMenuOpenForId === rating.id ? null : rating.id;
  }

  selectRating(rating: AssetRating){
    this.selectedAssetRating = {...rating};
    this.openEditModal();
  }

  openAddModal(){
    this.selectedAssetRating = new AssetRating();
    this.resetInputFields();
    this.isEdit = false;
  }

  openEditModal(){
    this.resetInputFields();
    this.isEdit = true;
    this.fillInputFields();
  }

  private resetInputFields(){
    this.selectedRating = null;
    this.selectedUnit = '';
    this.selectedRatingId = null;
  }

  private fillInputFields() {
    const rawRatingWithUnit = this.selectedAssetRating?.kvaRating ?? '';
    const [ratingString, unitString] = rawRatingWithUnit.split(' ');
    const rating = Number(ratingString);

    this.selectedRating = isNaN(rating) ? 0 : rating;
    const unitLower = unitString?.toLowerCase();
    if (unitLower === 'kva') {
      this.selectedUnit = 'kVA';
    } else if (unitLower === 'mva') {
      this.selectedUnit = 'MVA';
    } else {
      this.selectedUnit = '';
    }
    this.selectedRatingId = this.selectedAssetRating?.assetRatingId ?? null;
  }

  save() {
    if(this.selectedRating === null){
      this.toastr.warning("Rating can't be empty!");
      return;
    }
    if(this.selectedUnit === ''){
      this.toastr.warning("Please select a unit!");
      return;
    }
    if (this.selectedRatingId && !/^[a-zA-Z0-9]*$/.test(this.selectedRatingId)) {
      this.toastr.warning('Only alphanumeric characters are allowed for ID!');
      return;
    }
    this.selectedAssetRating.utilityId = this.utilityId;
    this.selectedAssetRating.assetRatingId =
      this.selectedRatingId && this.selectedRatingId.trim() !== ''
        ? this.selectedRatingId
        : undefined;
    this.normalizeSelectAssetRatingValue();
    this.assetRatingService.save(this.selectedAssetRating).subscribe({
      next: (response) => {
        if (response.status === 200 || response.status === 'OK') {
          this.closeEditModal?.nativeElement?.click();
          const message = response.message.replace(/Asset/g, 'Transformer');
          this.toastr.success(message);
          if (this.selectedAssetRating.id === 0) {
            this.onPageChange(1);
            return;
          }
          this.reloadTable();
        } else {
          const message = response.message.replace(/Asset/g, 'Transformer');
          this.toastr.error(message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  onRemove(){
    this.assetRatingService.delete(this.selectedAssetRating.id).subscribe({
      next: (response) => {
        if (response.status === 200 || response.status === 'OK') {
          this.onPageChange(1);
          const message = response.message.replace(/Asset/g, 'Transformer');
          this.toastr.success(message);
        } else {
          const message = response.message.replace(/Asset/g, 'Transformer');
          this.toastr.error(message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  private normalizeSelectAssetRatingValue() {
    this.selectedAssetRating.kvaRating = this.selectedRating + ' ' + this.selectedUnit;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.reloadTable()
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    this.reloadTable()
  }

  @HostListener('document:click')
  closeActionMenu() {
    if (this.actionMenuOpenForId !== null) {
      this.actionMenuOpenForId = null;
    }
  }
}
